import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { formatCurrency } from 'support/helpers/currency/currency';
import { countDecimalPlaces } from 'support/helpers/numbers/numbers';
import { useDocumentCurrency } from 'components/WebEDI/helpers';

import { RoundedCurrencyTooltip } from '../RoundedCurrency';
import { useTaxBreakdownRows } from '../taxBreakdown';
import { Summary } from '../types';

type OrderSummaryValues = {
  totalQuantity: number;
  subtotal: number;
  charges: number;
  allowances: number;
  taxes: number;
  due: number;
  net: number;
};

export const useOrderSummary = (): Summary => {
  const { t } = useTranslation();
  const currency = useDocumentCurrency();
  const values = useOrderSummaryValues();
  const taxBreakdown = useTaxBreakdownRows(currency);

  return useMemo(() => {
    return {
      content: [
        [
          {
            label: t('webedi:documentSummary.order.totalQuantity.label'),
            value: Math.round(values.totalQuantity),
          },
          {
            label: t(`webedi:documentSummary.order.grossAmount.label`),
            value: <span className="tabular-nums">{formatCurrency(values.subtotal, currency)}</span>,
            requiresConfig: 'showPriceFields' as const,
          },
          {
            label: t(`webedi:documentSummary.order.allowances.label`),
            value: <span className="tabular-nums">- {formatCurrency(values.allowances, currency)}</span>,
            requiresConfig: 'showPriceFields' as const,
          },
          {
            label: t(`webedi:documentSummary.order.charges.label`),
            value: <span className="tabular-nums">+ {formatCurrency(values.charges, currency)}</span>,
            requiresConfig: 'showPriceFields' as const,
          },
        ],
        [
          {
            label: t(`webedi:documentSummary.order.net.label`),
            value: <span className="tabular-nums">{formatCurrency(values.net, currency)}</span>,
            requiresConfig: 'showPriceFields' as const,
          },
          {
            label: t(`webedi:documentSummary.order.taxes.label`),
            value: <span className="tabular-nums">+ {formatCurrency(values.taxes, currency)}</span>,
            subItems: taxBreakdown.length ? taxBreakdown : undefined,
            requiresConfig: 'showPriceFields' as const,
          },
        ],
      ],
      footer: {
        label: t(`webedi:documentSummary.order.footer.label`),
        requiresConfig: 'showPriceFields' as const,
        value:
          countDecimalPlaces(values.due) > 2 ? (
            <RoundedCurrencyTooltip value={values.due} currency={currency} type={'Order'} />
          ) : (
            <span className="tabular-nums">
              {formatCurrency(values.due, currency, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          ),
      },
    };
  }, [
    currency,
    t,
    values.allowances,
    values.charges,
    values.due,
    values.net,
    values.subtotal,
    values.taxes,
    values.totalQuantity,
    taxBreakdown,
  ]);
};

const calculateLineItemsQuantity = (lineItems: Array<any> | undefined): number => {
  return (
    lineItems?.reduce((acc, item) => {
      return acc + (item.ordered_quantity ?? 0);
    }, 0) || 0
  );
};

// We are calculating a more user-friendly summary for the invoice here:
// Basically we are reporting charges and allowances separately from the subtotal
// regardless of whether they are on line- or document level.
// Typically the subtotal would already have charges and allowances from the line-level included.
const calculateInvoiceAndCreditNoteSummary = (
  initialSummary: OrderSummaryValues,
  lineItems?: Array<any>,
): OrderSummaryValues =>
  lineItems?.reduce(
    (acc, item) => ({
      ...acc,
      subtotal: acc.subtotal + item.line_gross_amount,
      charges: acc.charges + item.line_charge_amount,
      allowances: acc.allowances + item.line_allowance_amount,
    }),
    initialSummary,
  ) ?? initialSummary;

const useOrderSummaryValues = (): OrderSummaryValues => {
  const [lineItems, due, net, taxes, charges, allowances] = useWatch({
    name: [
      'line_items',
      'info.0.due_amount',
      'info.0.net_amount',
      'tax.total_tax_amount',
      'info.0.charge_amount',
      'info.0.allowance_amount',
    ],
  });

  return useMemo(
    () =>
      calculateInvoiceAndCreditNoteSummary(
        {
          subtotal: 0,
          charges: charges ?? 0,
          allowances: allowances ?? 0,
          taxes: taxes ?? 0,
          due: due ?? 0,
          net: net ?? 0,
          totalQuantity: calculateLineItemsQuantity(lineItems),
        },
        lineItems,
      ),
    [charges, allowances, taxes, due, net, lineItems],
  );
};

/*
 * i18n
 *
 * t("webedi:documentSummary.order.footer.tooltip")
 */
